import React, { useEffect, useState } from 'react';
import DaumPostcode, { Address } from "react-daum-postcode";
import axios, { AxiosResponse } from 'axios';
import PropTypes from 'prop-types';
import styled from '@emotion/styled';
import Container from '../elements/Container';
import StyledBox from '../elements/Box';
import Text from '../elements/Text';
import { KakaoAddressDocument } from '@/common/types';

import { designSystem } from '@/styles/theme';

// 카카오 REST API 키 - 환경변수로 관리하는 것을 권장합니다
// Kakao Developers(https://developers.kakao.com/)에서 발급받아 사용하세요
const kakao_api_key = import.meta.env.VITE_KAKAO_API_KEY || "eb7f1a627baec35a927860ead3752768";

// Kakao API 전용 axios 인스턴스 생성 (전역 interceptor 영향 받지 않도록)
const kakaoAxios = axios.create({
  withCredentials: false
});

interface AddressSearchFiledProps {
  width: string;
  height: number;
  onChange: (value: KakaoAddressDocument) => void;
  border: string;
  placeholder?: string;
  defaultValue?: string;
  icon?: React.ReactNode;
  type?: string;
  position?: string;
  margin?: string;
  borderBottom?: string;
  /**
   * 폼 상자형 검색 창(세미나 등록 폼) — 입력칸 아래 8px 띄우고 테두리 #D3DBD8·반경 12, 오른쪽 위 [닫기].
   * 기본(false)은 예전 모양 그대로(다른 화면 영향 없음).
   */
  formPopup?: boolean;
}

const PopupHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 4px 4px 8px 6px;
  font-size: 13px;
  font-weight: 700;
  color: #4F5B58;

  button {
    min-height: 32px;
    padding: 0 10px;
    border: 1px solid #D3DBD8;
    border-radius: 16px;
    background: #fff;
    font-family: inherit;
    font-size: 13px;
    font-weight: 600;
    color: #4F5B58;
    cursor: pointer;
  }
`;

export const SearchBox = styled.div<{ width: string, height: number, border: string, margin?: string }>`
  width: ${(props) => props.width ? props.width : "100%"};
  cursor: pointer;
  border-radius: 10px;
  padding: 0 19px;
  color: #a2a2a2;
  height: ${(props) => props.height ?? 48}px;
  border: ${(props) => props.border ?? "none"};
  background-color: #fff;
  display: grid;
  align-content: center;
  margin: ${(props) => props.margin};

  @media (max-width: 768px) {
    padding: 0 14px;
    border-radius: 8px;
  }
`;

const AddressSearchFiled = ({ width, height, onChange, border, icon, margin, defaultValue, formPopup = false }: AddressSearchFiledProps) => {
        const [address, setAddress] = useState(defaultValue || "주소를 검색해 주세요");
        const [result, setResult] = useState<KakaoAddressDocument | null>(null);
        const [daumPostOpen, setDaumPostOpen] = useState(false);
      
        const handleComplete = async (data: Address) => {
          const { address } = data;
          setAddress(address);
        };
      
        const handleClick = () => {
          setDaumPostOpen(!daumPostOpen);
        };
      
        const handleAddressResultChange = (res: KakaoAddressDocument) => {
          setResult(res);
          onChange(res);
        };
      
        useEffect(() => {
          if (address && address !== "주소를 검색해 주세요") {
            kakaoAxios.get(`https://dapi.kakao.com/v2/local/search/address.json?query=${encodeURIComponent(address)}`, {
              headers: { Authorization: `KakaoAK ${kakao_api_key}` }
            }).then((res: AxiosResponse) => {
              const addressData: KakaoAddressDocument = res.data.documents?.[0];
              handleAddressResultChange(addressData);
            }).catch(() => {
              // dapi 좌표 조회 실패해도 검색창은 닫고 선택한 주소는 유지(좌표는 없을 수 있음).
              setDaumPostOpen(false);
            });
          }
        }, [address]);
      
        useEffect(() => {
          if (defaultValue) {
            setAddress(defaultValue); // defaultValue가 있을 때 address를 업데이트
          }
        }, [defaultValue]);
      
        useEffect(() => {
          setDaumPostOpen(false);
        }, [result]);
      
        return (
          <>
            <SearchBox border={border} onClick={() => handleClick()} width={width} height={height} margin={margin}>
              <Container gap={"12px"} display={"flex"} alignitems={"center"}>
                {icon && icon}
                <Text size='17px' textcolor={result === null ? "#8B8D94" : "#444"}>{address}</Text>
              </Container>
            </SearchBox>
            {daumPostOpen && (formPopup ? (
              <StyledBox boxposition='default' border='1px solid #D3DBD8' borderradius='12px' padding='8px' margin='8px 0 0' bgcolor='#fff' overflow='hidden'>
                <PopupHead>
                  <span>주소 검색</span>
                  <button type="button" onClick={() => setDaumPostOpen(false)}>닫기</button>
                </PopupHead>
                <DaumPostcode onComplete={handleComplete} autoClose={false} />
              </StyledBox>
            ) : (
              <StyledBox boxposition='default' border={`1px solid ${designSystem.colors.primary}`} padding='3px' margin='0 0 5px'>
                <DaumPostcode onComplete={handleComplete} autoClose={false} />
              </StyledBox>
            ))}
          </>
        );
      };

export default AddressSearchFiled;

AddressSearchFiled.propTypes = {
  width: PropTypes.string,
  height: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  border: PropTypes.string,
  placeholder: PropTypes.string,
  defaultValue: PropTypes.string,
  icon: PropTypes.element,
  type: PropTypes.string,
  position: PropTypes.func,
};
